import React, { useMemo, useState } from 'react';
import { Alert, Button, FormHelperText, Skeleton, Stack, Typography } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import {
	useDisableDebtorAutomationMutation,
	useEnableDebtorAutomationMutation,
	useGetDebtorAutomationQuery,
	useRunDebtorAutomationSyncMutation,
} from '@/features/deals/debtorAutomationApi';
import { useGetDealPoliciesQuery } from '@/features/deals/dealsApi';
import FolioCheckbox from '@/components/FolioCheckbox';
import { SwitchLovely } from '@/components/SwitchLovely';
import { useDispatch } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import { useObjectContext } from '@/hooks/useObjectContext';
import { safelyParseDate } from '@/utils/date';
import DebtorRemindersSection from './UpcomingRemindersSection';

const POSITIVE_BALANCE_REQUIRED_MESSAGE = 'Invoice with a positive balance is required';

const getSyncStateMessage = (syncState) => {
	switch (syncState) {
		case 'Synced':
			return 'Reminders are current';
		case 'OutOfDate':
			return 'Changes are waiting to sync';
		case 'SyncFailed':
			return 'Failed to sync';
		case 'SyncInProgress':
			return 'Sync in progress';
		default:
			return 'Reminders are off';
	}
};

const formatRelativeUpdate = (lastUpdated) => {
	if (!lastUpdated) {
		return 'Last updated unavailable';
	}

	const parsedDate = safelyParseDate(lastUpdated);

	if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
		return 'Last updated unavailable';
	}

	return `Last updated ${formatDistanceToNow(parsedDate, { addSuffix: true })}`;
};

/**
 * Component for managing debtor automation settings on a deal, including enabling/disabling automated reminders and syncing with Folio debtor management.
 * Displays the current automation status, last updated time, upcoming reminder schedule, and handles user interactions with optimistic UI updates and error handling.
 * @param {string} dealId - The doc id of the deal to manage debtor automation for.
 */
export const DebtorAutomationPanel = () => {
	const dispatch = useDispatch();
	const { dealId } = useObjectContext();
	const {
		data: automation,
		isLoading,
		isError,
	} = useGetDebtorAutomationQuery({ dealId }, { skip: !dealId });
	const showAutomationLoading = isLoading && !automation;
	const showAutomationError = isError && !automation;
	const policiesQuery = useGetDealPoliciesQuery({ dealId }, { skip: !dealId });

	const [actionError, setActionError] = useState('');
	const [coolingDown, setCoolingDown] = useState(false);

	const [enableAutomation, enableMutation] = useEnableDebtorAutomationMutation();
	const [disableAutomation, disableMutation] = useDisableDebtorAutomationMutation();
	const [runSync, syncMutation] = useRunDebtorAutomationSyncMutation();

	const statusMessage = useMemo(
		() => getSyncStateMessage(automation?.syncState),
		[automation?.syncState],
	);
	const hasEligibleInvoices = useMemo(
		() =>
			(policiesQuery.data?.transactions ?? []).some(
				(transaction) => Number(transaction?.Balance ?? 0) > 0,
			),
		[policiesQuery.data?.transactions],
	);
	const isCheckingInvoiceEligibility =
		!dealId || (!policiesQuery.isError && !policiesQuery.isSuccess);

	const isMissingEligibleInvoice =
		!isCheckingInvoiceEligibility && !policiesQuery.isError && !hasEligibleInvoices;

	const isMutating = enableMutation.isLoading || disableMutation.isLoading || coolingDown;

	const isSyncLocked = automation?.syncState === 'SyncInProgress';
	const automationEnabled = Boolean(automation?.automationEnabled);
	//Reminders with 0 dollar are toggled off aswell with nightly sync
	const remindersOn = automationEnabled && !isMissingEligibleInvoice;

	const automationDisabled =
		isMutating || isSyncLocked || isCheckingInvoiceEligibility || isMissingEligibleInvoice;

	const syncDisabled =
		syncMutation.isLoading || isCheckingInvoiceEligibility || !automation?.automationEnabled;

	const handleToggle = async () => {
		if (!dealId || automationDisabled) {
			return;
		}

		setActionError('');
		setCoolingDown(true);

		try {
			if (automation?.automationEnabled) {
				await disableAutomation({ dealId }).unwrap();
			} else {
				await enableAutomation({ dealId }).unwrap();
			}
		} catch (error) {
			console.error('Error updating debtor automation', error);
			setActionError(
				error?.data?.error ??
					error?.data?.message ??
					error?.error ??
					error?.message ??
					'Unable to update automated reminders.',
			);
		} finally {
			setTimeout(() => setCoolingDown(false), 6000);
		}
	};

	const handleSync = async () => {
		if (!dealId || syncDisabled) {
			return;
		}

		try {
			await runSync({ dealId }).unwrap();
			dispatch(
				showToast({
					message: 'Invoice status is updated.',
					success: true,
				}),
			);
		} catch (error) {
			console.error('Error updating invoice status', error);
			dispatch(
				showToast({
					message: 'Unable to update invoice status.',
					error: true,
				}),
			);
		}
	};

	const automatedRemindersSublabel = isMissingEligibleInvoice
		? POSITIVE_BALANCE_REQUIRED_MESSAGE
		: isCheckingInvoiceEligibility
			? 'Checking invoice eligibility...'
			: isMutating
				? 'Updating automation status...'
				: isSyncLocked
					? 'Sync in progress'
					: statusMessage;

	return (
		<>
			<Stack spacing={1.5}>
				<Typography variant='form_label'>Automated reminders</Typography>
				{isLoading ? (
					<Stack spacing={1.25}>
						<Skeleton height='3.5em' />
						<Skeleton height='1.25em' width='40%' />
						<Skeleton height='6em' />
					</Stack>
				) : isError ? (
					<Alert severity='error' sx={{ alignItems: 'center' }}>
						Unable to load automation details.
					</Alert>
				) : (
					<Stack spacing={1.5}>
						<FolioCheckbox
							component={SwitchLovely}
							checked={remindersOn}
							onChange={handleToggle}
							disabled={automationDisabled}
							label={`Automated reminders ${remindersOn ? 'on' : 'off'}`}
							sublabel={automatedRemindersSublabel}
						/>
						<Typography variant='body2' color='text.secondary'>
							{formatRelativeUpdate(automation?.lastUpdated)}
						</Typography>
						{actionError && <FormHelperText error>{actionError}</FormHelperText>}
						<Stack spacing={1}>
							<Button
								variant='outlined'
								color='info'
								onClick={handleSync}
								disabled={syncDisabled}
								sx={{ alignSelf: 'flex-start' }}
							>
								{syncMutation.isLoading
									? 'Updating Invoice Status...'
									: 'Update Invoice Status'}
							</Button>
						</Stack>
						<DebtorRemindersSection />
					</Stack>
				)}
			</Stack>
		</>
	);
};

export default DebtorAutomationPanel;
